import { HttpInterceptorFn, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { isHttpAuthFailure } from '../utils/auth-errors';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

/**
 * Marks a request that must go out without our Cognito token.
 *
 * Presigned S3 URLs already carry their credentials in the query string, and S3
 * rejects a request that also sends an `Authorization` header ("only one auth
 * mechanism allowed"). A bearer token would additionally leak to a host that has
 * no business seeing it.
 */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  const authService = inject(AuthService);
  const token = authService.getToken();

  if (token && !authService.isTokenValid()) {
    authService.handleSessionExpired();
    return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Session expired', url: req.url }));
  }

  if (!token) {
    console.warn('No authentication token available');
  }

  const authReq = token
    ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
    : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (isHttpAuthFailure(error)) {
        authService.handleSessionExpired();
      }
      return throwError(() => error);
    })
  );
};
